import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { buildWallpaperDataUri, parseWallpaperUri } from "./toDataUri";
import { MOTIFS, PALETTES } from "./wallpaperGenerators";
import WallpaperColorStrip from "./WallpaperColorStrip";

// "Preset wallpapers" grid for the Wallpaper picker (deck 01-wallpaper-svg-library,
// notes7 rework). Gordon's explicit decision: "do not show the same SVG pattern
// multiple times with different colors." So every DESIGN appears ONCE (a motif grid),
// and selecting a design reveals a Color-theme strip that recolors it — NOT a
// 40-tile recolor grid. The registry (5 motifs x 8 palettes) is still the backing
// store: a tile's data-URI is buildWallpaperDataUri(`${paletteId}-${motifId}`), so
// persistence + selection-recovery are unchanged.
//
// Each tile is a real <button> — keyboard-focusable, aria-labelled, aria-pressed —
// satisfying the clickable-image a11y gate (G-20). Selecting a tile (or a palette)
// calls onSelect(dataUri), stored in the existing bgWallpaper image slot
// (currentBg === 2).

const DEFAULT_PALETTE_ID = PALETTES[0].id; // "violet" (brand)

const Section = styled.div`
  margin-top: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 4px 8px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: "DM Sans", sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #123051;
`;

const CountPill = styled.span`
  font-family: "DM Sans", sans-serif;
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.4px;
  color: #7e4fd6;
  background: #f3ecff;
  border-radius: 999px;
  padding: 3px 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const Tile = styled.button`
  position: relative;
  padding: 0;
  border: ${({ selected }) =>
    selected ? "2px solid #9B5AF8" : "1px solid #e4e7ec"};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #f1f2f4;
  aspect-ratio: 1 / 1;
  line-height: 0;
  transition: border-color 0.12s ease;

  &:hover {
    border-color: ${({ selected }) => (selected ? "#9B5AF8" : "#c7a8f0")};
  }
  &:focus-visible {
    outline: 2px solid #9b5af8;
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const TileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CheckBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #9b5af8;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Note = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  color: #667085;
  margin: 8px 4px 2px;
`;

// Line-art check (never an emoji) for the selected badge.
const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20 6L9 17l-5-5"
      fill="none"
      stroke="#ffffff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Recover { paletteId, motifId } from a stored library value. parseWallpaperUri
// returns the registry id `${paletteId}-${motifId}`; both tokens are single
// lowercase words so a single split is unambiguous.
const recover = (value) => {
  const id = parseWallpaperUri(value);
  if (!id) return { paletteId: null, motifId: null };
  const [paletteId, motifId] = id.split("-");
  return { paletteId, motifId };
};

function WallpaperLibraryGrid({ bgWallpaper, onSelect }) {
  const recovered = useMemo(() => recover(bgWallpaper), [bgWallpaper]);

  // Active palette for the whole grid (every design previews in this theme). The
  // selected motif is what a tile's aria-pressed reflects.
  const [paletteId, setPaletteId] = useState(
    recovered.paletteId || DEFAULT_PALETTE_ID
  );
  const [selectedMotifId, setSelectedMotifId] = useState(recovered.motifId);

  // If the host swaps in a different saved wallpaper (e.g. switching segments),
  // re-sync the editor to it.
  useEffect(() => {
    if (recovered.motifId) {
      setSelectedMotifId(recovered.motifId);
      if (recovered.paletteId) setPaletteId(recovered.paletteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recovered.paletteId, recovered.motifId]);

  // Precompute the 5 tile previews for the active palette (not per-render per-tile).
  const previews = useMemo(
    () =>
      MOTIFS.map((m) => ({
        motifId: m.id,
        label: m.label,
        dataUri: buildWallpaperDataUri(`${paletteId}-${m.id}`),
      })),
    [paletteId]
  );

  const applyDesign = (motifId, palId) => {
    setSelectedMotifId(motifId);
    onSelect(buildWallpaperDataUri(`${palId}-${motifId}`));
  };

  const handleSelectMotif = (motifId) => applyDesign(motifId, paletteId);

  const handleSelectPalette = (palId) => {
    setPaletteId(palId);
    // Recolor the currently-selected design in the new theme (no-op with no
    // selection yet — the strip only shows once a design is chosen).
    if (selectedMotifId) applyDesign(selectedMotifId, palId);
  };

  return (
    <Section data-cy="wallpaper-svg-library">
      <Header>
        <Title>Preset wallpapers</Title>
        <CountPill>{MOTIFS.length} DESIGNS</CountPill>
      </Header>
      <Grid>
        {previews.map((w) => {
          const selected = selectedMotifId === w.motifId;
          return (
            <Tile
              type="button"
              key={w.motifId}
              selected={selected}
              aria-pressed={selected}
              aria-label={`Select ${w.label} wallpaper`}
              title={w.label}
              onClick={() => handleSelectMotif(w.motifId)}
            >
              <TileImg src={w.dataUri} alt="" draggable={false} />
              {selected && (
                <CheckBadge>
                  <CheckIcon />
                </CheckBadge>
              )}
            </Tile>
          );
        })}
      </Grid>

      {selectedMotifId && (
        <WallpaperColorStrip
          palettes={PALETTES}
          value={paletteId}
          onChange={handleSelectPalette}
        />
      )}

      <Note>Every tile is generated art — no upload needed.</Note>
    </Section>
  );
}

export default WallpaperLibraryGrid;
